import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Input, DateInput, FormField, Textarea } from '@/components/ui/Input.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { Badge } from '@/components/ui/Badge.jsx';
import { DataTable } from '@/components/DataTable.jsx';
import { EmployeePicker } from '@/components/EmployeePicker.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { formatDate } from '@/lib/format.js';
import { Plus, Check, X } from 'lucide-react';

export default function LeavePage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [openBal, setOpenBal] = useState(false);
  const [draft, setDraft] = useState({ employee_id: '', leave_type_id: '', from_date: '', to_date: '', reason: '' });
  const [bal, setBal] = useState({ employee_id: '', leave_type_id: '', year: new Date().getFullYear(), opening: 0 });

  const { data: requests = [], isLoading } = useQuery({ queryKey: ['leaveRequests', {}], queryFn: () => api.get('/leave/requests') });
  const { data: balances = [] } = useQuery({ queryKey: ['leaveBalances', 'all'], queryFn: () => api.get('/leave/balances?all=1') });
  const { data: types = [] } = useQuery({ queryKey: ['leaveTypes'], queryFn: () => api.get('/leave-types') });

  // Derive available balance for the currently selected employee + leave type
  const draftYear = draft.from_date ? new Date(draft.from_date).getFullYear() : new Date().getFullYear();
  const availableBalance = useMemo(() => {
    if (!draft.employee_id || !draft.leave_type_id) return null;
    const rec = balances.find(
      (b) => b.employee_id === Number(draft.employee_id) &&
             b.leave_type_id === Number(draft.leave_type_id) &&
             b.year === draftYear,
    );
    return rec ? Number(rec.opening) - Number(rec.used) : 0;
  }, [balances, draft.employee_id, draft.leave_type_id, draftYear]);

  const requestedDays = useMemo(() => {
    if (!draft.from_date || !draft.to_date) return 0;
    const diff = Math.round((new Date(draft.to_date) - new Date(draft.from_date)) / 86400000) + 1;
    return diff > 0 ? diff : 0;
  }, [draft.from_date, draft.to_date]);

  const selectedType = useMemo(
    () => types.find((t) => t.id === Number(draft.leave_type_id)),
    [types, draft.leave_type_id],
  );
  const isNoPay = selectedType ? !selectedType.paid : false;

  const balanceExceeded = !isNoPay && availableBalance !== null && requestedDays > availableBalance;

  const create = useMutation({
    mutationFn: () => api.post('/leave/requests', { ...draft, employee_id: Number(draft.employee_id), leave_type_id: Number(draft.leave_type_id) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['leaveRequests', {}] }); setOpen(false); toast({ variant: 'success', title: 'Created' }); },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });
  const decide = useMutation({
    mutationFn: ({ id, status }) => api.post(`/leave/requests/${id}/decision`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['leaveRequests', {}] }); qc.invalidateQueries({ queryKey: ['leaveBalances', 'all'] }); toast({ variant: 'success', title: 'Updated' }); },
  });
  const upsertBal = useMutation({
    mutationFn: () => api.post('/leave/balances', { ...bal, employee_id: Number(bal.employee_id), leave_type_id: Number(bal.leave_type_id), year: Number(bal.year), opening: Number(bal.opening) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['leaveBalances', 'all'] }); setOpenBal(false); toast({ variant: 'success', title: 'Saved' }); },
  });

  const reqCols = [
    { key: 'emp_no', header: 'Emp No', className: 'font-mono', sortable: true },
    { key: 'full_name', header: 'Name', sortable: true },
    { key: 'leave_code', header: 'Type', className: 'font-mono' },
    { key: 'from_date', header: 'From', sortable: true, render: (r) => formatDate(r.from_date), className: 'font-mono' },
    { key: 'to_date', header: 'To', render: (r) => formatDate(r.to_date), className: 'font-mono' },
    { key: 'days', header: 'Days', className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'reason', header: 'Reason' },
    { key: 'status', header: 'Status',
      render: (r) => <Badge variant={r.status === 'approved' ? 'success' : r.status === 'rejected' ? 'danger' : 'warning'}>{r.status}</Badge>,
    },
    { key: '__a', header: '', className: 'w-32 text-right',
      render: (r) => r.status === 'pending' ? (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => decide.mutate({ id: r.id, status: 'approved' })}><Check className="h-4 w-4 text-emerald-600" /></Button>
          <Button variant="ghost" size="icon" onClick={() => decide.mutate({ id: r.id, status: 'rejected' })}><X className="h-4 w-4 text-red-500" /></Button>
        </div>
      ) : null },
  ];

  const balCols = [
    { key: 'emp_no', header: 'Emp No', className: 'font-mono', sortable: true },
    { key: 'full_name', header: 'Name', sortable: true },
    { key: 'leave_name', header: 'Type' },
    { key: 'year', header: 'Year', className: 'font-mono' },
    { key: 'opening', header: 'Opening', className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'used', header: 'Used', className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'balance', header: 'Balance', accessor: (r) => r.opening - r.used, render: (r) => (r.opening - r.used).toFixed(1), className: 'text-right tabular-nums font-mono font-semibold', headClassName: 'text-right' },
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Leave Management" description="Requests, approvals and balances"
        actions={<>
          <Button variant="outline" onClick={() => setOpenBal(true)}>Set Balance</Button>
          <Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" />New Request</Button>
        </>}
      />

      <Tabs defaultValue="requests">
        <TabsList>
          <TabsTrigger value="requests">Requests</TabsTrigger>
          <TabsTrigger value="balances">Balances</TabsTrigger>
        </TabsList>
        <TabsContent value="requests"><Section><DataTable columns={reqCols} data={requests} loading={isLoading} pageSize={15} exportFilename="leave-requests.csv" /></Section></TabsContent>
        <TabsContent value="balances"><Section><DataTable columns={balCols} data={balances} pageSize={15} exportFilename="leave-balances.csv" /></Section></TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Leave Request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Employee" required><EmployeePicker value={draft.employee_id} onChange={(v) => setDraft({ ...draft, employee_id: v })} /></FormField>
            <FormField label="Leave Type" required>
              <Select value={draft.leave_type_id} onChange={(e) => setDraft({ ...draft, leave_type_id: e.target.value })}>
                <option value="">— select —</option>
                {types.map((t) => <option key={t.id} value={t.id}>{t.code} — {t.name}</option>)}
              </Select>
            </FormField>
            {availableBalance !== null && !isNoPay && (
              <div className={`text-sm px-3 py-2 rounded-md ${
                balanceExceeded ? 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-400' : 'bg-muted text-muted-foreground'
              }`}>
                Available balance: <span className="font-semibold tabular-nums">{availableBalance}</span> day(s)
                {requestedDays > 0 && <> &nbsp;·&nbsp; Requested: <span className="font-semibold tabular-nums">{requestedDays}</span> day(s)</>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="From" required><DateInput value={draft.from_date} onChange={(e) => setDraft({ ...draft, from_date: e.target.value })} /></FormField>
              <FormField label="To" required><DateInput value={draft.to_date} onChange={(e) => setDraft({ ...draft, to_date: e.target.value })} /></FormField>
            </div>
            <FormField label="Reason"><Textarea value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} /></FormField>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => create.mutate()} disabled={create.isPending || balanceExceeded}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openBal} onOpenChange={setOpenBal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Set Leave Balance</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Employee" required><EmployeePicker value={bal.employee_id} onChange={(v) => setBal({ ...bal, employee_id: v })} /></FormField>
            <FormField label="Leave Type" required>
              <Select value={bal.leave_type_id} onChange={(e) => setBal({ ...bal, leave_type_id: e.target.value })}>
                <option value="">— select —</option>
                {types.map((t) => <option key={t.id} value={t.id}>{t.code} — {t.name}</option>)}
              </Select>
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Year"><Input type="number" value={bal.year} onChange={(e) => setBal({ ...bal, year: e.target.value })} /></FormField>
              <FormField label="Opening Days"><Input type="number" step="0.5" value={bal.opening} onChange={(e) => setBal({ ...bal, opening: e.target.value })} /></FormField>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => upsertBal.mutate()} disabled={upsertBal.isPending}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
